import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoalsDB } from '@/hooks/useGoalsDB';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useJourneyProgress } from '@/hooks/useJourneyProgress';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';

import { GoalSettingTab } from '@/components/dashboard/GoalSettingTab';
import { HabitTrackingTab } from '@/components/dashboard/HabitTrackingTab';
import { CoreValuesTab } from '@/components/dashboard/CoreValuesTab';
import { DevPlanTab } from '@/components/dashboard/DevPlanTab';
import { MentorshipTab } from '@/components/dashboard/MentorshipTab';
import { OnboardingWelcome } from '@/components/onboarding/OnboardingWelcome';
import { OnboardingTour } from '@/components/OnboardingTour';
import { JourneyNavigation } from '@/components/journey/JourneyNavigation';
import { PhaseContextBanner } from '@/components/journey/PhaseContextBanner';
import { JourneyWizard } from '@/components/journey/JourneyWizard';
import { PhaseCompletionBanner } from '@/components/journey/PhaseCompletionBanner';
import { ValuesGateBanner } from '@/components/journey/ValuesGateBanner';
import { useToast } from '@/hooks/use-toast';
const Index = () => {
  const { goals, completedGoals, loading, addGoal, updateGoal, deleteGoal, completeGoal, addHabit, toggleHabitForToday, getTotalStreak, getTodayProgress, refetch: refetchGoals } = useGoalsDB();
  const { user } = useAuth();
  const { profile, invalidate: invalidateProfile } = useProfile();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const journey = useJourneyProgress(goals);

  // Get initial tab from location state (when navigating from Home) or default to 'values'
  const initialTab = (location.state as { activeTab?: string })?.activeTab || 'values';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showWizard, setShowWizard] = useState(false);
  const [expandedMilestoneIndex, setExpandedMilestoneIndex] = useState<number | null>(null);

  // Handle checkout success/cancel redirects
  useEffect(() => {
    const checkoutStatus = searchParams.get("checkout");
    if (checkoutStatus === "success") {
      toast({
        title: "Welcome to Pro! 🎉",
        description: "Your subscription is now active. Enjoy all Pro features!",
      });
      navigate("/dashboard", { replace: true });
    } else if (checkoutStatus === "canceled") {
      toast({
        title: "Checkout canceled",
        description: "No worries! You can upgrade anytime.",
        variant: "default",
      });
      navigate("/dashboard", { replace: true });
    }
  }, [searchParams, toast, navigate]);

  // Update active tab when navigating from Home with state
  useEffect(() => {
    const state = location.state as { activeTab?: string; expandedMilestoneIndex?: number } | null;
    if (state?.activeTab && state.activeTab !== 'home') {
      setActiveTab(state.activeTab);
      // Handle expandedMilestoneIndex from navigation state
      if (state.expandedMilestoneIndex !== undefined) {
        setExpandedMilestoneIndex(state.expandedMilestoneIndex);
      }
      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // NOTE: JourneyWizard is now opened via OnboardingTour's end prompt (for new users)
  // or manually via the Map button in header. Removed auto-open to prevent overlap.

  const handleWizardClose = () => {
    setShowWizard(false);
    localStorage.setItem('momentum-wizard-seen', 'true');
  };

  const handleTabChange = (tab: string) => {
    if (tab === 'home') {
      // Navigate to Home page
      navigate('/home');
    } else {
      setActiveTab(tab);
      // Scroll to top so user sees the new tab content immediately
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Navigate to Plan tab and expand a specific milestone
  const handleNavigateToPlan = (milestoneIndex: number) => {
    setExpandedMilestoneIndex(milestoneIndex);
    setActiveTab('plan');
  };

  // Check if values phase is complete (determines if Home tab should show)
  const valuesPhaseComplete = journey.phases.find(p => p.id === 'values')?.isComplete ?? false;
  const showHomeTab = !journey.isLoading && valuesPhaseComplete;

  // Welcome gate: show OnboardingWelcome for brand-new users who haven't been prompted yet
  const valuesPhase = journey.phases.find(p => p.id === 'values');
  const profileAny = profile as Record<string, unknown> | null;
  const isNewUser = !journey.isLoading
    && (valuesPhase?.progress === 0);
  const needsWelcome = isNewUser
    && profileAny !== null
    && profileAny?.enhanced_coaching_prompted !== true;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onOpenWizard={() => setShowWizard(true)} valuesComplete={valuesPhaseComplete} />
      <main id="main-content" className="container py-8 pb-24 space-y-6">
        {/* Welcome interstitial for brand-new users */}
        {needsWelcome ? (
          <OnboardingWelcome onComplete={() => invalidateProfile()} />
        ) : (
        <>
        {/* Unified Journey Navigation */}
        <JourneyNavigation
          phases={journey.phases}
          overallProgress={journey.overallProgress}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          showHomeTab={showHomeTab}
        />

        {/* Phase completion celebration */}
        {!journey.isLoading && (
          <PhaseCompletionBanner phases={journey.phases} />
        )}

        <OnboardingTour
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">

          {/* Phase Context Banner */}
          {journey.currentPhase && (
            <PhaseContextBanner
              currentPhase={journey.currentPhase}
              phases={journey.phases}
              activeTab={activeTab}
              onNavigate={handleTabChange}
            />
          )}

          <TabsContent value="values">
            <CoreValuesTab onValuesChanged={() => journey.refetch()} onNavigateToGoals={() => handleTabChange('goals')} />
          </TabsContent>
          <TabsContent value="goals">
            <GoalSettingTab
              goals={goals}
              completedGoals={completedGoals}
              valuesComplete={valuesPhaseComplete}
              onNavigateToValues={() => setActiveTab('values')}
              onAddGoal={async (...args) => {
                const result = await addGoal(...args);
                journey.refetch();
                return result;
              }}
              onUpdateGoal={updateGoal}
              onDeleteGoal={async (id) => {
                await deleteGoal(id);
                journey.refetch();
              }}
              onCompleteGoal={async (id) => {
                const success = await completeGoal(id);
                if (success) journey.refetch();
                return success;
              }}
              onAddHabit={addHabit}
              onToggleHabit={toggleHabitForToday}
              totalStreak={getTotalStreak()}
              todayProgress={getTodayProgress()}
              onNavigateToPlan={handleNavigateToPlan}
              onTabChange={handleTabChange}
            />
          </TabsContent>
          <TabsContent value="plan">
            {!valuesPhaseComplete && <ValuesGateBanner onNavigateToValues={() => setActiveTab('values')} />}
            <DevPlanTab
              expandedMilestoneIndex={expandedMilestoneIndex}
              onMilestoneExpanded={() => setExpandedMilestoneIndex(null)}
              onPlanGenerated={() => journey.refetch()}
              onHabitsAccepted={refetchGoals}
            />
          </TabsContent>
          <TabsContent value="habits">
            <HabitTrackingTab
              goals={goals}
              valuesComplete={valuesPhaseComplete}
              hasPlan={journey.phases.find(p => p.id === 'plan')?.isComplete ?? false}
              onNavigateToValues={() => setActiveTab('values')}
              onToggleHabit={async (goalId, habitId) => {
                await toggleHabitForToday(goalId, habitId);
                journey.refetch();
              }} 
              totalStreak={getTotalStreak()} 
              todayProgress={getTodayProgress()}
              onNavigateToPlan={handleNavigateToPlan}
            />
          </TabsContent>
          <TabsContent value="mentorship">
            <MentorshipTab />
          </TabsContent>
        </Tabs>

        {/* Journey Wizard Modal */}
        <JourneyWizard
          phases={journey.phases}
          isOpen={showWizard}
          onClose={handleWizardClose}
          onNavigate={(tab) => {
            handleTabChange(tab);
            handleWizardClose();
          }}
        />
        </>
        )}
      </main>
    </div>
  );
};

export default Index;
